import { useEffect, useMemo, useRef, useState } from "react";

interface PlayerNameComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  /** Visual cue that the value needs admin attention (e.g. unmapped player). */
  invalid?: boolean;
  /** ARIA label, used when the field isn't wrapped in a <label>. */
  ariaLabel?: string;
}

/**
 * Free-text combobox for player names: the value is a string (not an id), so
 * the admin can type a brand new name that doesn't exist yet AND pick from the
 * suggestion list of existing players + melee aliases.
 */
export function PlayerNameCombobox({
  value,
  onChange,
  options,
  placeholder = "Nome do jogador",
  invalid = false,
  ariaLabel,
}: PlayerNameComboboxProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options
      .filter((o) => o.toLowerCase().includes(q))
      .slice(0, 50);
  }, [value, options]);

  useEffect(() => {
    setHighlight(0);
  }, [value, open]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-idx="${highlight}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  function select(opt: string) {
    onChange(opt);
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) select(opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  return (
    <div className="relative w-full" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-label={ariaLabel}
        value={value}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className={[
          "w-full rounded border px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500",
          invalid
            ? "border-amber-400 bg-amber-50"
            : "border-slate-300",
        ].join(" ")}
      />
      {open && filtered.length > 0 && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-auto rounded border border-slate-200 bg-white shadow-lg"
        >
          {filtered.map((opt, idx) => (
            <li
              key={`${opt}-${idx}`}
              data-idx={idx}
              role="option"
              aria-selected={value === opt}
              onMouseDown={(e) => {
                e.preventDefault();
                select(opt);
              }}
              onMouseEnter={() => setHighlight(idx)}
              className={[
                "px-3 py-1.5 cursor-pointer text-sm",
                idx === highlight
                  ? "bg-brand-50 text-brand-900"
                  : "text-slate-700",
                value === opt ? "font-medium" : "",
              ].join(" ")}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
